if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      pendingSelectedOptionValues = [];
      isRestoringHistory = false;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];
      mobileStickyMediaElements = undefined;
      mobileStickyMediaMql = undefined;
      mobileStickyMediaResizeObserver = undefined;
      mobileStickyMediaUpdateFrame = undefined;
      mobileStickyShadowUpdateFrame = undefined;

      constructor() {
        super();

        this.quantityInput = this.querySelector('.quantity__input');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        this.initializeVariantHistory();
        this.initializeMobileStickyMedia();
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
        window.removeEventListener('popstate', this.onVariantHistoryPop);
        this.destroyMobileStickyMedia();
      }

      initializeMobileStickyMedia() {
        const product = this.querySelector('.product');
        if (!product) return;

        this.mobileStickyMediaMql = window.matchMedia('(max-width: 749px)');
        this.onMobileStickyMediaViewportChange = this.queueMobileStickyMediaUpdate.bind(this);
        this.mobileStickyMediaMql.addEventListener('change', this.onMobileStickyMediaViewportChange);
        window.addEventListener('resize', this.onMobileStickyMediaViewportChange, { passive: true });
        this.onMobileStickyMediaScroll = this.queueMobileStickyShadowUpdate.bind(this);
        window.addEventListener('scroll', this.onMobileStickyMediaScroll, { passive: true });

        if ('ResizeObserver' in window) {
          this.mobileStickyMediaResizeObserver = new ResizeObserver(() => this.queueMobileStickyMediaUpdate());
        }

        this.bindMobileStickyMediaElements();
        document.fonts?.ready.then(() => this.queueMobileStickyMediaUpdate());
      }

      bindMobileStickyMediaElements() {
        this.mobileStickyMediaResizeObserver?.disconnect();

        const product = this.querySelector('.product');
        const mediaWrapper = product?.querySelector('.product__media-wrapper');
        const gallery = mediaWrapper?.querySelector('media-gallery.product__column-sticky');
        const infoWrapper = product?.querySelector('.product__info-wrapper');
        const variantPicker = infoWrapper?.querySelector('variant-selects');
        const stickyHeader = document.querySelector('sticky-header');

        this.mobileStickyMediaElements = { product, mediaWrapper, gallery, infoWrapper, variantPicker, stickyHeader };

        if (!product || !mediaWrapper || !gallery || !infoWrapper || !variantPicker) {
          this.clearMobileStickyMedia();
          return;
        }

        [gallery, stickyHeader, ...Array.from(infoWrapper.querySelectorAll('.product__info-container > *'))]
          .filter(Boolean)
          .forEach((element) => this.mobileStickyMediaResizeObserver?.observe(element));
        this.queueMobileStickyMediaUpdate();
      }

      queueMobileStickyMediaUpdate() {
        if (this.mobileStickyMediaUpdateFrame) return;
        this.mobileStickyMediaUpdateFrame = requestAnimationFrame(() => {
          this.mobileStickyMediaUpdateFrame = undefined;
          this.updateMobileStickyMedia();
        });
      }

      updateMobileStickyMedia() {
        const { product, gallery, infoWrapper, variantPicker, stickyHeader } = this.mobileStickyMediaElements || {};
        if (!this.mobileStickyMediaMql?.matches || !product || !gallery || !infoWrapper || !variantPicker) {
          this.clearMobileStickyMedia();
          return;
        }

        const previousGalleryTop = gallery.getBoundingClientRect().top;
        const preserveGalleryPosition = product.classList.contains('product--mobile-sticky-media');
        const galleryHeight = gallery.getBoundingClientRect().height;
        const stickyRange = variantPicker.getBoundingClientRect().top - infoWrapper.getBoundingClientRect().top;
        const stickyTop = Math.max(0, stickyHeader?.getBoundingClientRect().bottom || 0);

        if (galleryHeight <= 0 || stickyRange <= 0) {
          this.clearMobileStickyMedia();
          return;
        }

        product.style.setProperty('--mobile-product-gallery-height', `${galleryHeight}px`);
        product.style.setProperty('--mobile-product-gallery-range', `${stickyRange}px`);
        product.style.setProperty('--mobile-product-gallery-top', `${stickyTop}px`);
        product.classList.add('product--mobile-sticky-media');

        if (preserveGalleryPosition) {
          const galleryShift = gallery.getBoundingClientRect().top - previousGalleryTop;
          if (Math.abs(galleryShift) > 0.5) window.scrollBy(0, galleryShift);
        }
        this.queueMobileStickyShadowUpdate();
      }

      queueMobileStickyShadowUpdate() {
        if (this.mobileStickyShadowUpdateFrame) return;
        this.mobileStickyShadowUpdateFrame = requestAnimationFrame(() => {
          this.mobileStickyShadowUpdateFrame = undefined;
          this.updateMobileStickyShadow();
        });
      }

      updateMobileStickyShadow() {
        const { product, mediaWrapper, gallery } = this.mobileStickyMediaElements || {};
        if (
          !this.mobileStickyMediaMql?.matches ||
          !product?.classList.contains('product--mobile-sticky-media') ||
          !mediaWrapper ||
          !gallery
        ) {
          product?.classList.remove('product--mobile-sticky-media-shadow');
          return;
        }

        const mediaWrapperRect = mediaWrapper.getBoundingClientRect();
        const galleryRect = gallery.getBoundingClientRect();
        const stickyTop = parseFloat(product.style.getPropertyValue('--mobile-product-gallery-top')) || 0;
        const contentIsSlidingUnder =
          mediaWrapperRect.top < stickyTop - 1 &&
          Math.abs(galleryRect.top - stickyTop) < 2 &&
          mediaWrapperRect.bottom > galleryRect.bottom + 1;

        product.classList.toggle('product--mobile-sticky-media-shadow', contentIsSlidingUnder);
      }

      clearMobileStickyMedia() {
        const product = this.mobileStickyMediaElements?.product || this.querySelector('.product');
        if (!product) return;
        product.classList.remove('product--mobile-sticky-media');
        product.classList.remove('product--mobile-sticky-media-shadow');
        product.style.removeProperty('--mobile-product-gallery-height');
        product.style.removeProperty('--mobile-product-gallery-range');
        product.style.removeProperty('--mobile-product-gallery-top');
      }

      destroyMobileStickyMedia() {
        if (this.mobileStickyMediaUpdateFrame) cancelAnimationFrame(this.mobileStickyMediaUpdateFrame);
        if (this.mobileStickyShadowUpdateFrame) cancelAnimationFrame(this.mobileStickyShadowUpdateFrame);
        this.mobileStickyMediaResizeObserver?.disconnect();
        this.mobileStickyMediaMql?.removeEventListener('change', this.onMobileStickyMediaViewportChange);
        window.removeEventListener('resize', this.onMobileStickyMediaViewportChange);
        window.removeEventListener('scroll', this.onMobileStickyMediaScroll);
        this.clearMobileStickyMedia();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        this.pendingSelectedOptionValues = selectedOptionValues;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.setSubmitButtonUpdating(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector), this.pendingSelectedOptionValues);
          this.updateURL(productUrl, variant?.id, this.pendingSelectedOptionValues);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            return callback(html);
          })
          .then(() => {
            // set focus to last clicked option value
            if (targetId) document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else {
              this.productForm?.setSubmitButtonUpdating(false);
              console.error(error);
            }
          });
      }

      getSelectedVariant(productInfoNode, selectedOptionValues = []) {
        if (!this.getSelectionState(productInfoNode, selectedOptionValues).complete) return null;

        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      hasCompleteOptionSelection(productInfoNode, selectedOptionValues) {
        return this.getSelectionState(productInfoNode, selectedOptionValues).complete;
      }

      getSelectionState(productInfoNode, selectedOptionValues = []) {
        const variantSelects = productInfoNode?.querySelector('variant-selects');
        if (!variantSelects) return { complete: true, missingOptionNames: [] };

        const selectedIds = new Set(selectedOptionValues.filter(Boolean).map(String));
        const optionControls = Array.from(variantSelects.querySelectorAll('fieldset[data-option-name], select[data-option-name]'));
        const missingOptionNames = optionControls
          .filter(
            (control) =>
              !Array.from(control.querySelectorAll('[data-option-value-id]')).some((option) =>
                selectedIds.has(String(option.dataset.optionValueId))
              )
          )
          .map((control) => control.dataset.optionName);

        return { complete: optionControls.length > 0 && missingOptionNames.length === 0, missingOptionNames };
      }

      getIncompleteSelectionText(missingOptionNames) {
        const normalizedNames = missingOptionNames.map((name) =>
          name.toLocaleLowerCase().replace(/ß/g, 'ss').normalize('NFD').replace(/[\u0300-\u036f]/g, '')
        );
        const missingColor = normalizedNames.some((name) => ['farbe', 'color', 'colour'].includes(name));
        const missingSize = normalizedNames.some((name) => ['grosse', 'size'].includes(name));
        const isGerman = document.documentElement.lang.toLocaleLowerCase().startsWith('de');
        const chooseColor = window.variantStrings.chooseColor || (isGerman ? 'Farbe wählen' : 'Choose color');
        const chooseSize = window.variantStrings.chooseSize || (isGerman ? 'Größe wählen' : 'Choose size');
        const chooseColorAndSize =
          window.variantStrings.chooseColorAndSize || (isGerman ? 'Farbe und Größe wählen' : 'Choose color and size');
        const chooseOptionsNamed =
          window.variantStrings.chooseOptionsNamed || (isGerman ? '[options] wählen' : 'Choose [options]');

        if (missingColor && missingSize) return chooseColorAndSize;
        if (missingColor) return chooseColor;
        if (missingSize) return chooseSize;
        return chooseOptionsNamed.replace('[options]', missingOptionNames.join(', '));
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html, selectedOptionValues = []) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
          const selectedIds = new Set(selectedOptionValues.filter(Boolean));
          this.variantSelectors.querySelectorAll('option').forEach((option) => {
            option.selected = false;
            option.removeAttribute('selected');
          });
          this.variantSelectors.querySelectorAll('[data-option-value-id]').forEach((option) => {
            const selected = selectedIds.has(option.dataset.optionValueId);
            if (option.matches('input')) option.checked = selected;
            if (option.matches('option')) {
              option.selected = selected;
              option.toggleAttribute('selected', selected);
            }
          });
          this.variantSelectors.applySizeAvailability?.();
        }
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const selection = this.getSelectionState(html, this.pendingSelectedOptionValues);
          const variant = selection.complete ? this.getSelectedVariant(html, this.pendingSelectedOptionValues) : null;

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html, this.pendingSelectedOptionValues);
          this.updateURL(productUrl, variant?.id, this.pendingSelectedOptionValues);
          this.updateVariantInputs(variant?.id);

          const previewMediaId =
            this.variantSelectors?.querySelector('input:checked[data-featured-media-id], option[selected][data-featured-media-id]')
              ?.dataset.featuredMediaId || variant?.featured_media?.id;
          const mediaUpdate = this.updateMedia(html, previewMediaId);

          if (!variant) {
            this.setUnavailable(
              selection.complete ? window.variantStrings.unavailable : this.getIncompleteSelectionText(selection.missingOptionNames),
              selection.complete ? 'invalid' : 'incomplete'
            );
            return mediaUpdate;
          }

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

          const sourceSubmitButton = html.getElementById(`ProductSubmitButton-${this.sectionId}`);
          const isSoldOut = sourceSubmitButton?.hasAttribute('disabled') ?? true;
          this.productForm?.setSelectionState({
            state: isSoldOut ? 'sold-out' : 'available',
            text: isSoldOut ? window.variantStrings.soldOut : window.variantStrings.addToCart,
          });

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateURL(url, variantId, selectedOptionValues = []) {
        this.querySelector('share-button')?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`
        );

        if (this.dataset.updateUrl === 'false') return;
        const historyState = {
          ...window.history.state,
          variantGallery: { productId: this.dataset.productId, optionValues: selectedOptionValues },
        };
        const historyUrl = `${url}${variantId ? `?variant=${variantId}` : ''}`;
        if (this.isRestoringHistory) {
          this.isRestoringHistory = false;
          return;
        }
        window.history.pushState(historyState, '', historyUrl);
      }

      setUnavailable(text = window.variantStrings.unavailable, state = 'invalid') {
        this.productForm?.setSelectionState({ state, text });

        const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      async updateMedia(html, variantFeaturedMediaId) {
        const currentGallery = this.querySelector('media-gallery');
        const nextGallery = html.querySelector('media-gallery');
        if (!currentGallery || !nextGallery) return;

        const leadImage = nextGallery.querySelector(
          variantFeaturedMediaId
            ? `[data-media-id="${this.sectionId}-${variantFeaturedMediaId}"] img`
            : 'li[data-media-id] img'
        ) || nextGallery.querySelector('li[data-media-id] img');
        if (leadImage) {
          leadImage.loading = 'eager';
          try {
            await leadImage.decode();
          } catch (error) {
            // A failed decode still leaves the fetched image available to the browser.
          }
        }

        const scrollX = window.scrollX;
        const scrollY = window.scrollY;
        const wrapper = currentGallery.parentElement;
        const previousHeight = currentGallery.getBoundingClientRect().height;
        if (previousHeight) wrapper.style.minHeight = `${previousHeight}px`;
        currentGallery.replaceWith(nextGallery);

        const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
        const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
        if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
        if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);
        window.scrollTo(scrollX, scrollY);
        requestAnimationFrame(() =>
          requestAnimationFrame(() => {
            wrapper.style.removeProperty('min-height');
            this.bindMobileStickyMediaElements();
          })
        );
      }

      initializeVariantHistory() {
        if (this.dataset.updateUrl === 'false') return;
        this.onVariantHistoryPop = this.handleVariantHistoryPop.bind(this);
        window.addEventListener('popstate', this.onVariantHistoryPop);
        const optionValues = this.variantSelectors?.selectedOptionValues || [];
        window.history.replaceState(
          { ...window.history.state, variantGallery: { productId: this.dataset.productId, optionValues } },
          '',
          window.location.href
        );
      }

      handleVariantHistoryPop(event) {
        const state = event.state?.variantGallery;
        if (!state || state.productId !== this.dataset.productId) return;
        this.pendingSelectedOptionValues = state.optionValues || [];
        this.isRestoringHistory = true;
        this.resetProductFormState();
        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(this.dataset.url, this.pendingSelectedOptionValues),
          targetId: '',
          callback: this.handleUpdateProductInfo(this.dataset.url),
        });
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart .loading__spinner').classList.remove('hidden');
        fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() => this.querySelector('.quantity__rules-cart .loading__spinner').classList.add('hidden'));
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
          }
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list'
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}
